/**
 * InstitutionsManagement - Admin component for managing institutions (hospitals, schools, etc.)
 */
import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { institutionsAPI, getErrorMessage } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { MediaUpload } from '@/components/MediaUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Globe,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  Building2,
  GripVertical,
  Image as ImageIcon,
  Upload,
  Loader2,
  X,
  Search,
  Filter,
  RotateCcw,
  Hospital,
  GraduationCap,
  Building,
  Briefcase
} from 'lucide-react';
import GoogleMapPicker from '@/components/GoogleMapPicker';

// Institution types with icons and labels
const INSTITUTION_TYPES = [
  { value: 'hospital', label: 'Hospital', icon: Hospital },
  { value: 'school', label: 'School', icon: GraduationCap },
  { value: 'university', label: 'University', icon: GraduationCap },
  { value: 'clinic', label: 'Clinic', icon: Hospital },
  { value: 'research_center', label: 'Research Center', icon: Building2 },
  { value: 'government', label: 'Government', icon: Building },
  { value: 'ngo', label: 'NGO', icon: Building2 },
  { value: 'corporate', label: 'Corporate', icon: Briefcase },
  { value: 'other', label: 'Other', icon: Building2 },
];

const getTypeIcon = (type) => {
  const found = INSTITUTION_TYPES.find(t => t.value === type);
  return found?.icon || Building2;
};

const getTypeLabel = (type) => {
  const found = INSTITUTION_TYPES.find(t => t.value === type);
  return found?.label || 'Other';
};

const InstitutionsManagement = () => {
  const queryClient = useQueryClient();
  const [showDialog, setShowDialog] = useState(false);
  const [editingInstitution, setEditingInstitution] = useState(null);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    country: '',
    city: '',
    institution_type: '',
    continent: '',
    region: '',
    district: '',
    is_global: null,
  });
  const [showFilters, setShowFilters] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    institution_type: 'other',
    website_url: '',
    logo_url: '',
    location: '',
    address: '',
    // Geographic hierarchy
    continent: '',
    country: '',
    region: '',
    district: '',
    city: '',
    neighborhood: '',
    // Coordinates
    latitude: '',
    longitude: '',
    // Contact
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    description: '',
    is_global: true
  });

  // Handle logo file upload
  const handleLogoUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload an image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('file_type', 'institution_logo');

      const response = await api.post('/admin/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.url) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${baseUrl}${response.data.url}`;
        setFormData(prev => ({ ...prev, logo_url: fullUrl }));
        toast.success('Logo uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  // Fetch institutions with filters
  const activeFilters = useMemo(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.country) params.country = filters.country;
    if (filters.city) params.city = filters.city;
    if (filters.continent) params.continent = filters.continent;
    if (filters.region) params.region = filters.region;
    if (filters.district) params.district = filters.district;
    if (filters.institution_type) params.institution_type = filters.institution_type;
    if (filters.is_global !== null) params.is_global = filters.is_global;
    return params;
  }, [filters]);

  const { data: institutions, isLoading, error: fetchError } = useQuery({
    queryKey: ['admin-institutions', activeFilters],
    queryFn: async () => {
      const r = await institutionsAPI.adminList(activeFilters);
      // Ensure we always return an array
      return Array.isArray(r.data) ? r.data : [];
    }
  });

  // Fetch locations for filter dropdowns
  const { data: locations } = useQuery({
    queryKey: ['institution-locations'],
    queryFn: async () => {
      try {
        const r = await institutionsAPI.getLocations();
        const data = r.data || {};
        // Extract just the names from the location objects
        return {
          continents: (data.continents || []).map(c => c.name).filter(Boolean),
          countries: (data.countries || []).map(c => c.name).filter(Boolean),
          cities: (data.cities || []).map(c => c.name).filter(Boolean),
          regions: (data.regions || []).map(r => r.name).filter(Boolean),
          districts: (data.districts || []).map(d => d.name).filter(Boolean),
        };
      } catch {
        return { countries: [], cities: [], continents: [], regions: [], districts: [] };
      }
    }
  });

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      country: '',
      city: '',
      continent: '',
      region: '',
      district: '',
      institution_type: '',
      is_global: null,
    });
  };

  // Check if any filters are active
  const hasActiveFilters = filters.search || filters.country || filters.city || filters.continent || filters.region || filters.district || filters.institution_type || filters.is_global !== null;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => institutionsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-institutions']);
      toast.success('Institution created successfully');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to create institution'));
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => institutionsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-institutions']);
      toast.success('Institution updated successfully');
      handleCloseDialog();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to update institution'));
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => institutionsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-institutions']);
      toast.success('Institution deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Failed to delete institution'));
    }
  });

  const handleOpenCreate = () => {
    setEditingInstitution(null);
    setFormData({
      name: '',
      website_url: '',
      logo_url: '',
      location: '',
      address: '',
      continent: '',
      country: '',
      region: '',
      district: '',
      city: '',
      neighborhood: '',
      latitude: '',
      longitude: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      description: '',
      is_global: true
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (institution) => {
    setEditingInstitution(institution);
    setFormData({
      name: institution.name || '',
      website_url: institution.website_url || '',
      logo_url: institution.logo_url || '',
      location: institution.location || '',
      address: institution.address || '',
      continent: institution.continent || '',
      country: institution.country || '',
      region: institution.region || institution.state || '',
      district: institution.district || '',
      city: institution.city || '',
      neighborhood: institution.neighborhood || '',
      latitude: institution.latitude || '',
      longitude: institution.longitude || '',
      contact_name: institution.contact_name || '',
      contact_email: institution.contact_email || '',
      contact_phone: institution.contact_phone || '',
      description: institution.description || '',
      is_global: institution.is_global !== false
    });
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingInstitution(null);
    setFormData({
      name: '',
      website_url: '',
      logo_url: '',
      location: '',
      address: '',
      continent: '',
      country: '',
      region: '',
      district: '',
      city: '',
      neighborhood: '',
      latitude: '',
      longitude: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      description: '',
      is_global: true
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Institution name is required');
      return;
    }

    // Clean up the form data - convert empty strings to null for optional numeric fields
    const cleanedData = {
      ...formData,
      latitude: formData.latitude === '' ? null : parseFloat(formData.latitude) || null,
      longitude: formData.longitude === '' ? null : parseFloat(formData.longitude) || null,
    };

    if (editingInstitution) {
      updateMutation.mutate({ id: editingInstitution.id, data: cleanedData });
    } else {
      createMutation.mutate(cleanedData);
    }
  };

  const handleDelete = (institution) => {
    if (window.confirm(`Are you sure you want to delete "${institution.name}"?`)) {
      deleteMutation.mutate(institution.id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 mb-4">Failed to load institutions</p>
        <Button onClick={() => queryClient.invalidateQueries(['admin-institutions'])}>
          Try Again
        </Button>
      </div>
    );
  }

  // Ensure institutions is always an array for safe rendering
  const institutionsList = Array.isArray(institutions) ? institutions : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Institutions</h2>
          <p className="text-sm text-slate-500 mt-1">
            Manage partner institutions (hospitals, schools, clinics, etc.)
          </p>
        </div>
        <Button onClick={handleOpenCreate} data-testid="add-institution-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Institution
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col gap-4">
            {/* Search bar */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, city, country, or address..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="pl-10"
                  data-testid="institution-search-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-slate-100' : ''}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    !
                  </Badge>
                )}
              </Button>
              {hasActiveFilters && (
                <Button variant="ghost" size="icon" onClick={resetFilters} title="Reset filters">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filter dropdowns */}
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2 border-t">
                {/* Continent */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Continent</Label>
                  <Select
                    value={filters.continent || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, continent: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="continent-filter">
                      <SelectValue placeholder="All Continents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Continents</SelectItem>
                      {(locations?.continents || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Country */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Country</Label>
                  <Select
                    value={filters.country || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, country: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="country-filter">
                      <SelectValue placeholder="All Countries" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Countries</SelectItem>
                      {(locations?.countries || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Region/State */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Region/State</Label>
                  <Select
                    value={filters.region || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, region: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="region-filter">
                      <SelectValue placeholder="All Regions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      {(locations?.regions || []).map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* District */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">District</Label>
                  <Select
                    value={filters.district || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, district: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="district-filter">
                      <SelectValue placeholder="All Districts" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Districts</SelectItem>
                      {(locations?.districts || []).map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">City</Label>
                  <Select
                    value={filters.city || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, city: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="city-filter">
                      <SelectValue placeholder="All Cities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {(locations?.cities || []).map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Scope */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Scope</Label>
                  <Select
                    value={filters.is_global === null ? 'all' : filters.is_global ? 'global' : 'event'}
                    onValueChange={(value) => setFilters(prev => ({ 
                      ...prev, 
                      is_global: value === 'all' ? null : value === 'global' 
                    }))}
                  >
                    <SelectTrigger data-testid="scope-filter">
                      <SelectValue placeholder="All Scope" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Scope</SelectItem>
                      <SelectItem value="global">Global Only</SelectItem>
                      <SelectItem value="event">Event-Specific Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Institution Type */}
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Type</Label>
                  <Select
                    value={filters.institution_type || 'all'}
                    onValueChange={(value) => setFilters(prev => ({ ...prev, institution_type: value === 'all' ? '' : value }))}
                  >
                    <SelectTrigger data-testid="type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {INSTITUTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{institutionsList.length}</p>
                <p className="text-sm text-slate-500">Total Institutions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Globe className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {institutionsList.filter(c => c.is_global).length}
                </p>
                <p className="text-sm text-slate-500">Global (Shown Everywhere)</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {institutionsList.filter(c => !c.is_global).length}
                </p>
                <p className="text-sm text-slate-500">Event-Specific</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Institutions Grid */}
      {institutionsList.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">No institutions added yet</p>
            <p className="text-sm text-slate-500 mt-1">Add your first institution to get started</p>
            <Button onClick={handleOpenCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Add Institution
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {institutionsList.map(institution => {
            const TypeIcon = getTypeIcon(institution.institution_type);
            return (
            <Card key={institution.id} className="relative group" data-testid={`institution-card-${institution.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {institution.logo_url ? (
                      <img 
                        src={institution.logo_url.startsWith('http') ? institution.logo_url : `${process.env.REACT_APP_BACKEND_URL}${institution.logo_url}`}
                        alt={institution.name}
                        className="h-12 w-12 object-contain rounded-lg border"
                      />
                    ) : (
                      <div className="h-12 w-12 bg-slate-100 rounded-lg flex items-center justify-center">
                        <TypeIcon className="h-6 w-6 text-slate-400" />
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-lg">{institution.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs capitalize">
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {getTypeLabel(institution.institution_type)}
                        </Badge>
                        {institution.is_global ? (
                          <Badge variant="secondary" className="text-xs">
                            <Globe className="h-3 w-3 mr-1" />
                            Global
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">Event-Specific</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleOpenEdit(institution)}>
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      {institution.website_url && (
                        <DropdownMenuItem onClick={() => window.open(institution.website_url, '_blank')}>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Website
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => handleDelete(institution)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-slate-600">
                {institution.location && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{institution.location}</span>
                  </div>
                )}
                {institution.contact_phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-slate-400" />
                    <span>{institution.contact_phone}</span>
                  </div>
                )}
                {institution.contact_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-slate-400" />
                    <span>{institution.contact_email}</span>
                  </div>
                )}
                {institution.website_url && (
                  <div className="flex items-center gap-2">
                    <ExternalLink className="h-4 w-4 text-slate-400" />
                    <a 
                      href={institution.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline truncate"
                    >
                      {institution.website_url.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingInstitution ? 'Edit Institution' : 'Add New Institution'}
            </DialogTitle>
            <DialogDescription>
              {editingInstitution 
                ? 'Update the institution information below'
                : 'Enter the details for the new institution/assembly'
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Institution Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter institution name"
                  data-testid="institution-name-input"
                />
              </div>

              <div>
                <Label htmlFor="institution_type">Institution Type</Label>
                <Select
                  value={formData.institution_type}
                  onValueChange={(value) => setFormData({ ...formData, institution_type: value })}
                >
                  <SelectTrigger data-testid="institution-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {INSTITUTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="website_url">Website URL</Label>
                <Input
                  id="website_url"
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="logo_url">Institution Logo</Label>
                <div className="mt-1">
                  <MediaUpload
                    value={formData.logo_url}
                    onChange={(url) => setFormData({ ...formData, logo_url: url })}
                    purpose="logo"
                    testId="institution-logo-upload"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>

              {/* Geographic Hierarchy Section */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Geographic Location
                </h4>
              </div>

              <div>
                <Label htmlFor="continent">Continent</Label>
                <select
                  id="continent"
                  value={formData.continent}
                  onChange={(e) => setFormData({ ...formData, continent: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select Continent</option>
                  <option value="Africa">Africa</option>
                  <option value="Asia">Asia</option>
                  <option value="Europe">Europe</option>
                  <option value="North America">North America</option>
                  <option value="South America">South America</option>
                  <option value="Oceania">Oceania</option>
                </select>
              </div>

              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>

              <div>
                <Label htmlFor="region">Region/Province/State</Label>
                <Input
                  id="region"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  placeholder="State or Province"
                />
              </div>

              <div>
                <Label htmlFor="district">District/County</Label>
                <Input
                  id="district"
                  value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  placeholder="District or County"
                />
              </div>

              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="City"
                />
              </div>

              <div>
                <Label htmlFor="neighborhood">Neighborhood</Label>
                <Input
                  id="neighborhood"
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  placeholder="Neighborhood or Area"
                />
              </div>

              {/* Google Map Location Picker */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Map Location
                </h4>
                <GoogleMapPicker
                  latitude={formData.latitude ? parseFloat(formData.latitude) : null}
                  longitude={formData.longitude ? parseFloat(formData.longitude) : null}
                  address={formData.address || ''}
                  onLocationChange={(location) => {
                    setFormData(prev => ({
                      ...prev,
                      latitude: location.lat || '',
                      longitude: location.lng || '',
                      address: location.address || prev.address,
                      city: location.city || prev.city,
                      country: location.country || prev.country,
                      region: location.region || prev.region,
                      district: location.district || prev.district,
                      neighborhood: location.neighborhood || prev.neighborhood,
                      continent: location.continent || prev.continent,
                    }));
                  }}
                  height="250px"
                  placeholder="Search for institution location..."
                />
              </div>

              {/* Contact Section */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h4 className="text-sm font-medium text-slate-700 mb-3">Contact Information</h4>
              </div>

              <div>
                <Label htmlFor="contact_name">Contact Person</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Pastor John Doe"
                />
              </div>

              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="contact@institution.org"
                />
              </div>

              <div>
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the institution..."
                  rows={3}
                />
              </div>

              <div className="md:col-span-2 flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="is_global" className="font-medium">Global Visibility</Label>
                  <p className="text-sm text-slate-500">
                    Show this institution in header, footer, and directory across the entire app
                  </p>
                </div>
                <Switch
                  id="is_global"
                  checked={formData.is_global}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_global: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending}
                data-testid="save-institution-btn"
              >
                {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : 'Save Institution'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InstitutionsManagement;
